import { getMailApiBase } from "@/auth/paths";
import { mailAuthHeaders } from "@/auth/session";
import { mailApiGet, mailApiPost, type ApiResult } from "@/lib/api/client";
import type {
  MailContact,
  MailContactCollectEntry,
  MailContactSavePayload,
  MailContactStatus,
} from "@/lib/types/contact";

export function fetchMailContacts(params?: {
  status?: MailContactStatus | "all";
  q?: string;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.status && params.status !== "all") qs.set("status", params.status);
  if (params?.q?.trim()) qs.set("q", params.q.trim());
  if (params?.limit) qs.set("limit", String(params.limit));

  const query = qs.toString();
  return mailApiGet<{ success: boolean; contacts: MailContact[] }>(
    `contacts/list.php${query ? `?${query}` : ""}`
  );
}

export function collectMailContacts(entries: MailContactCollectEntry[]) {
  return mailApiPost<{ success: boolean; changed: number }>("contacts/collect.php", { entries });
}

export function saveMailContact(payload: MailContactSavePayload) {
  return mailApiPost<{ success: boolean; contact: MailContact }>("contacts/save.php", payload);
}

export function deleteMailContact(id: number) {
  return mailApiPost<{ success: boolean }>("contacts/delete.php", { id });
}

export function resolveMailContactPhotoUrl(relativeOrAbsolute: string | null | undefined): string | null {
  if (!relativeOrAbsolute) return null;
  if (relativeOrAbsolute.startsWith("blob:") || relativeOrAbsolute.startsWith("data:")) {
    return relativeOrAbsolute;
  }
  if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
  const rel = relativeOrAbsolute.replace(/^\//, "");
  return `${getMailApiBase()}/${rel}`;
}

export async function uploadMailContactPhoto(
  contactId: number,
  file: File
): Promise<ApiResult<{ success: boolean; photoUrl: string; photoVersion: number }>> {
  const form = new FormData();
  form.append("contactId", String(contactId));
  form.append("photo", file);

  const url = `${getMailApiBase()}/contacts/photo.php`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { ...mailAuthHeaders() },
    body: form,
  });

  let body: { success?: boolean; message?: string; photoUrl?: string; photoVersion?: number } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    body = {};
  }

  if (!res.ok || body.success === false) {
    return {
      ok: false,
      status: res.status,
      message: body.message || `Erreur HTTP ${res.status}`,
    };
  }

  return {
    ok: true,
    data: {
      success: true,
      photoUrl: body.photoUrl ?? "",
      photoVersion: body.photoVersion ?? 0,
    },
  };
}

export async function fetchMailContactPhotoBlob(relativeUrl: string): Promise<string | null> {
  const url = resolveMailContactPhotoUrl(relativeUrl);
  if (!url) return null;

  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: { ...mailAuthHeaders() },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
